import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useRouteContext } from '@tanstack/react-router'
import { useLatest } from 'ahooks'
import { Alert, App, Checkbox, Form, Input, Radio, type FormItemProps } from 'antd'
import dayjs from 'dayjs'
import type {
  DocumentAmendment,
  DocumentChoice,
  EnterpriseSummary,
  ServiceEnterpriseDraft
} from '@super-mega/server/contracts/order'
import { useTRPC } from '@/trpc/react.ts'
import { EnterpriseDocumentPicker, type DocumentDisplay } from './enterprise-document-picker.tsx'
import { EMPTY_DOCUMENTS } from './enterprise-labels.ts'

type DocumentNames = Partial<Record<'company' | 'identity', string | null | undefined>>

export function EnterpriseDocumentsEditor({
  value = EMPTY_DOCUMENTS,
  onChange,
  onReadyChange,
  disabled,
  prospective = false,
  current
}: {
  value?: DocumentAmendment
  onChange: (value: DocumentAmendment, names?: DocumentNames) => void
  onReadyChange?: (ready: boolean) => void
  disabled?: boolean
  prospective?: boolean
  current?: EnterpriseSummary
}) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { modal, message } = App.useApp()
  const { currentSession } = useRouteContext({ from: '/_authenticated' })
  const latest = useRef(value)
  useEffect(() => {
    latest.current = value
  }, [value])
  const changeDocuments = (next: DocumentAmendment, names?: DocumentNames) => {
    latest.current = next
    onChange(next, names)
  }
  const [ready, setReady] = useState({ company: true, identity: true })
  const latestReady = useRef(ready)
  const [company, setCompany] = useState<DocumentDisplay>()
  const [identity, setIdentity] = useState<DocumentDisplay>()
  const displays = useRef<{ company?: DocumentDisplay; identity?: DocumentDisplay }>({})
  const [bringingIdentity, setBringingIdentity] = useState(false)
  const readyCallback = useLatest(onReadyChange)
  useEffect(() => {
    // 隐藏执照候选时仅解除执照阻塞，身份证未完成仍阻止提交。
    readyCallback.current?.((prospective || ready.company) && ready.identity && !bringingIdentity)
  }, [prospective, ready.company, ready.identity, bringingIdentity, readyCallback])
  const readiness = (kind: 'company' | 'identity', next: boolean) => {
    const state = { ...latestReady.current, [kind]: next }
    latestReady.current = state
    setReady(state)
    onReadyChange?.((prospective || state.company) && state.identity)
  }
  const legalName =
    value.company && value.company.mode !== 'none'
      ? company?.legalRepresentative
      : current?.company?.legalRepresentative
  let identityName: string | null | undefined
  if (!value.clearPrimaryIdentity) {
    if (value.identity && value.identity.mode !== 'none') {
      identityName = identity?.name
    } else {
      identityName = current?.identity?.name
    }
  }
  const mismatch = Boolean(legalName && identityName && legalName.trim() !== identityName.trim())

  const selectDocument = (kind: 'company' | 'identity', choice: DocumentChoice, display?: DocumentDisplay) => {
    displays.current[kind] = display
    if (kind === 'company') {
      setCompany(display)
    } else {
      setIdentity(display)
    }
    const next = { ...latest.current, [kind]: choice }
    const companyName = (
      next.company && next.company.mode !== 'none'
        ? displays.current.company?.legalRepresentative
        : current?.company?.legalRepresentative
    )?.trim()
    const personName = (
      next.identity && next.identity.mode !== 'none' ? displays.current.identity?.name : current?.identity?.name
    )?.trim()
    next.confirmLegalRepresentative = Boolean(
      !prospective && !next.clearPrimaryIdentity && companyName && personName && companyName === personName
    )
    changeDocuments(next, { [kind]: display?.name })
    return next
  }

  const bringIdentity = async (id: number) => {
    setBringingIdentity(true)
    onReadyChange?.(false)
    try {
      const row = await cache.fetchQuery({ ...trpc.identity.reusable.queryOptions({ id }), staleTime: 0 })
      if (!row) {
        void message.warning('企业已关联的法人身份当前不可用，请核对')
      } else {
        latest.current = { ...latest.current, clearPrimaryIdentity: false }
        selectDocument(
          'identity',
          { mode: 'existing', id, expectedUpdatedAt: dayjs(row.updatedAt).toISOString() },
          { name: row.name }
        )
        readiness('identity', true)
        void message.info(`已带出企业关联法人：${row.name}，待最终提交`)
      }
    } catch (error) {
      void message.warning(error instanceof Error ? error.message : '法人资料读取失败，原身份选用已保留')
      onReadyChange?.((prospective || latestReady.current.company) && latestReady.current.identity)
    }
    setBringingIdentity(false)
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="mb-1 text-sm font-semibold">证件资料</h3>
        <p className="text-muted-foreground mb-3 text-sm">选择已有档案或上传证件识别；未修改的资料继续保留。</p>
      </div>
      <div className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2">
        {prospective ? (
          <section className="min-w-0 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h4 className="m-0 font-semibold">营业执照</h4>
                <p className="mt-1 mb-0 text-xs text-slate-500 dark:text-slate-400">登记后补录并核验企业资料</p>
              </div>
              <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                暂不提供
              </span>
            </div>
            <div className="flex flex-col gap-2 py-2">
              <p className="m-0 text-sm font-medium">取得营业执照后可在原企业补录</p>
              <p className="m-0 max-w-xs text-xs leading-6 text-slate-500 dark:text-slate-400">
                拟设立企业可以先创建订单，无需提供营业执照。登记完成后，在原企业档案中补录即可。
              </p>
            </div>
          </section>
        ) : (
          <EnterpriseDocumentPicker
            kind="company"
            current={
              current?.company
                ? {
                    name: current.company.name,
                    number: current.company.unifiedSocialCreditCode,
                    legalRepresentative: current.company.legalRepresentative
                  }
                : undefined
            }
            value={value.company}
            disabled={disabled || bringingIdentity}
            onReadyChange={(next) => readiness('company', next)}
            onChange={(choice, display, linkedIdentityId) => {
              const next = selectDocument('company', choice, display)
              if (!linkedIdentityId) {
                return
              }
              if (!currentSession.isSuperAdmin && !currentSession.permissions.includes('identity.read')) {
                void message.info('企业已有法人关系；身份详情受权限保护，无需重复建档')
                return
              }
              const selected = next.identity
              const existingId = selected?.mode === 'existing' ? selected.id : current?.primaryIdentityId
              if (existingId === linkedIdentityId) {
                return
              }
              if ((selected && selected.mode !== 'none') || existingId) {
                modal.confirm({
                  title: '企业已有不同的关联法人',
                  content: '是否用企业已关联的法人替换当前身份候选？取消将保留当前选用，提交前需要核对关系。',
                  okText: '替换身份候选',
                  cancelText: '保留当前身份',
                  onOk: () => bringIdentity(linkedIdentityId)
                })
              } else {
                void bringIdentity(linkedIdentityId)
              }
            }}
          />
        )}
        <EnterpriseDocumentPicker
          kind="identity"
          current={
            !value.clearPrimaryIdentity && current?.primaryIdentityId
              ? {
                  name: current.identity?.name ?? '已关联（资料受权限保护）',
                  number: current.identity?.maskedIdNumber
                }
              : undefined
          }
          value={value.identity}
          disabled={disabled || bringingIdentity || value.clearPrimaryIdentity}
          onReadyChange={(next) => readiness('identity', next)}
          onChange={(choice, display) => {
            selectDocument('identity', choice, display)
          }}
        />
      </div>
      {mismatch && (
        <Alert
          type="warning"
          showIcon
          title="执照法人姓名与当前身份姓名不一致"
          description="请更换资料；仅补执照时，请移除本次身份候选。原拟任法人发生变化时填写原因，并明确解除原身份选用。"
        />
      )}
      {!prospective && legalName && identityName && !value.clearPrimaryIdentity && (
        <section className="border-border border-t pt-4">
          <h3 className="mb-1 text-sm font-semibold">法人身份确认</h3>
          <p className="text-muted-foreground mb-3 text-sm">
            两份证件姓名一致时默认勾选，可手动取消；更换资料后会重新匹配。
          </p>
          {legalName && identityName && (
            <p className="mb-3 text-sm">
              执照法人：{legalName} · 身份证姓名：{identityName}
            </p>
          )}
          <Checkbox
            disabled={
              disabled || bringingIdentity || mismatch || value.clearPrimaryIdentity || !legalName || !identityName
            }
            checked={value.confirmLegalRepresentative}
            onChange={(event) => changeDocuments({ ...value, confirmLegalRepresentative: event.target.checked })}
          >
            将该身份证关联为执照上的法定代表人
          </Checkbox>
          <p className="text-muted-foreground mt-2 mb-0 text-xs">保存成功后关联生效；已有相同关联时无需重复确认。</p>
        </section>
      )}
      {current?.primaryIdentityId && (
        <Checkbox
          disabled={disabled || bringingIdentity}
          checked={value.clearPrimaryIdentity}
          onChange={(event) => {
            changeDocuments({
              ...value,
              clearPrimaryIdentity: event.target.checked,
              identity: { mode: 'none' },
              confirmLegalRepresentative: false
            })
            displays.current.identity = undefined
            setIdentity(undefined)
            readiness('identity', true)
          }}
        >
          解除原身份选用，仅保存本次企业资料
        </Checkbox>
      )}
      {current && (
        <Form.Item label="资料替换或解除原因" required={value.clearPrimaryIdentity}>
          <Input.TextArea
            aria-label="资料替换或解除原因"
            value={value.replacementReason}
            maxLength={500}
            rows={2}
            disabled={disabled || bringingIdentity}
            onChange={(event) => changeDocuments({ ...value, replacementReason: event.target.value || undefined })}
            placeholder="替换原有企业、法人资料或解除身份选用时必填"
          />
        </Form.Item>
      )}
    </div>
  )
}

export function EnterpriseDraftEditor({
  value,
  onChange,
  disabled,
  onReadyChange,
  hideDisplayName = false
}: {
  value: ServiceEnterpriseDraft
  onChange: (value: ServiceEnterpriseDraft) => void
  disabled?: boolean
  onReadyChange?: (ready: boolean) => void
  hideDisplayName?: boolean
}) {
  const { modal } = App.useApp()
  const latestDraft = useRef(value)
  useEffect(() => {
    latestDraft.current = value
  }, [value])
  const documentsReady = useRef(true)
  const documentNames = useRef<DocumentNames>({})
  const automaticName = useRef('')
  const changeDraft = (next: ServiceEnterpriseDraft, names?: DocumentNames) => {
    documentNames.current = { ...documentNames.current, ...names }
    const source = next.kind === 'registeredCompany' ? 'company' : 'identity'
    const suggestion = documentNames.current[source]?.trim() ?? ''
    // 只更新空称呼或本次自动带出的称呼，保留手动输入和重新打开时已有的称呼。
    const useSuggestion = !next.displayName.trim() || next.displayName === automaticName.current
    automaticName.current = suggestion
    const updated = { ...next, displayName: useSuggestion ? suggestion : next.displayName }
    latestDraft.current = updated
    onChange(updated)
  }
  return (
    <div className="flex flex-col gap-4">
      <Form.Item label="企业阶段" required className="mb-0!">
        <Radio.Group
          aria-label="企业阶段"
          optionType="button"
          buttonStyle="outline"
          value={value.kind}
          disabled={disabled}
          options={[
            { value: 'prospectiveCompany', label: '拟设立企业' },
            { value: 'registeredCompany', label: '已登记企业' }
          ]}
          onChange={(event) => {
            const kind = event.target.value as ServiceEnterpriseDraft['kind']
            const change = () => {
              changeDraft(
                {
                  ...latestDraft.current,
                  kind,
                  documents:
                    kind === 'prospectiveCompany'
                      ? {
                          ...latestDraft.current.documents,
                          company: { mode: 'none' },
                          confirmLegalRepresentative: false,
                          clearPrimaryIdentity: false
                        }
                      : latestDraft.current.documents
                },
                kind === 'prospectiveCompany' ? { company: undefined } : undefined
              )
            }
            if (
              kind === 'prospectiveCompany' &&
              ((value.documents?.company && value.documents.company.mode !== 'none') || !documentsReady.current)
            ) {
              modal.confirm({
                title: '切换为拟设立企业？',
                content: '本次营业执照候选将被移除，身份资料仍会保留。',
                onOk: change
              })
            } else {
              change()
            }
          }}
        />
      </Form.Item>
      {!hideDisplayName && <EnterpriseDisplayNameField value={value} onChange={onChange} disabled={disabled} />}
      <EnterpriseDocumentsEditor
        value={value.documents}
        onChange={(documents, names) => changeDraft({ ...latestDraft.current, documents }, names)}
        disabled={disabled}
        prospective={value.kind === 'prospectiveCompany'}
        onReadyChange={(ready) => {
          documentsReady.current = ready
          onReadyChange?.(ready)
        }}
      />
      <Form.Item label="备注" className="mb-0!">
        <Input.TextArea
          aria-label="备注"
          value={value.note ?? ''}
          disabled={disabled}
          maxLength={5000}
          rows={2}
          placeholder="选填，补充企业相关说明"
          onChange={(event) => onChange({ ...value, note: event.target.value })}
        />
      </Form.Item>
    </div>
  )
}

export function EnterpriseDisplayNameField({
  value,
  onChange,
  disabled,
  rules
}: {
  value: ServiceEnterpriseDraft
  onChange: (value: ServiceEnterpriseDraft) => void
  disabled?: boolean
  rules?: FormItemProps['rules']
}) {
  return (
    <Form.Item
      label="内部称呼"
      name={rules ? 'displayName' : undefined}
      required
      rules={rules}
      className="mb-0!"
      extra={
        value.kind === 'registeredCompany'
          ? '选用或识别营业执照后自动填入企业名称，也可手动修改。'
          : '选用或识别身份证后自动填入姓名；暂无资料时可手动填写。'
      }
    >
      <Input
        aria-label="内部称呼"
        value={value.displayName}
        disabled={disabled}
        maxLength={255}
        placeholder="根据下方资料自动填写，也可手动输入"
        onChange={(event) => onChange({ ...value, displayName: event.target.value })}
      />
    </Form.Item>
  )
}
